/**
 * The enable/disable switch for an automation — one component for every
 * surface that offers it (settings list rows, the record slideout header), so
 * the permission-gated tooltip treatment cannot drift between them.
 */

import { Toggle } from '@/lib/base/forms/toggle';
import { Tooltip } from '@/lib/base/tooltip/tooltip';

export interface AutomationEnabledToggleProps {
  enabled: boolean;
  /** Permission-gated: `hasPermission('ahoy_event_automation', 'ahoy_property_updated')`. */
  canToggle: boolean;
  /** True while a toggle write is in flight. */
  isBusy?: boolean;
  onChange: (isSelected: boolean) => void;
}

export function AutomationEnabledToggle({
  enabled,
  canToggle,
  isBusy = false,
  onChange,
}: AutomationEnabledToggleProps) {
  if (canToggle) {
    return (
      <Toggle
        isSelected={enabled}
        isDisabled={isBusy}
        aria-label={enabled ? 'Disable automation' : 'Enable automation'}
        onChange={onChange}
      />
    );
  }
  // `interactive` switches Tooltip to a non-button wrapper, so the disabled
  // Toggle (a switch/button) isn't nested inside another button — react-aria
  // would otherwise emit invalid nested interactive controls.
  return (
    <Tooltip
      title='Only admins have permission to edit automations'
      placement='top'
      interactive={true}
    >
      <Toggle
        isSelected={enabled}
        isDisabled
        aria-label={enabled ? 'Disable automation' : 'Enable automation'}
        onChange={onChange}
      />
    </Tooltip>
  );
}
